import { useState, type FormEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "../api/client";
import { Chips, List, ListRow } from "../components/List";
import { dateTime } from "../text";
import { useCampaign, useCampaignQuery, useDraft } from "./context";
import { CampaignError, SecretBadge, useAction, useEntries } from "./parts";
import type { CampaignEntryListItem, CampaignKind } from "./types";

/**
 * Entries by kind, with a filter by tag and a form for a new entry. A tag comes in by the address only from a known entry; the
 * filter chosen here stays in the page, so a tag seen in the dossier never reaches the history.
 */
export function EntriesPage() {
  const campaign = useCampaign();
  const { art } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  const tag = chosen === undefined ? search.get("schlagwort") : chosen;
  const all = useEntries();
  const entries = (all.data ?? []).filter((e) => (!art || e.kind === art) && (!tag || e.tags.some((t) => t.localeCompare(tag, "de", { sensitivity: "base" }) === 0)));
  const tags = [...new Set((all.data ?? []).filter((e) => !art || e.kind === art).flatMap((e) => e.tags))].sort((a, b) => a.localeCompare(b, "de"));
  const kinds = campaign.head.kinds.filter((k) => (all.data ?? []).some((e) => e.kind === k.id) || k.creatable);

  return (
    <section className="stack">
      <Chips
        ariaLabel="Art"
        allLabel="Alle"
        options={kinds.map((k) => ({ value: k.id, label: k.label.de }))}
        value={art ?? null}
        onChange={(value) => navigate(value ? `/k/${campaign.id}/eintraege/${value}` : `/k/${campaign.id}/eintraege`)}
      />
      {tags.length > 0 && (
        <Chips
          ariaLabel="Schlagwort"
          options={tags.map((t) => ({ value: t, label: t }))}
          value={tag}
          onChange={(value) => {
            setChosen(value);
            if (search.has("schlagwort")) setSearch({}, { replace: true });
          }}
        />
      )}
      {campaign.canWrite && <NewEntry kinds={campaign.head.kinds.filter((k) => k.creatable)} preferred={art} />}
      {all.isLoading && <p className="muted">Lade …</p>}
      {all.error && <CampaignError error={all.error} reload />}
      {all.data && entries.length === 0 && <p className="muted">Keine Einträge.</p>}
      <div className="card">
        <EntryList entries={entries} />
      </div>
    </section>
  );
}

export function EntryList({ entries, label = "Einträge" }: { entries: CampaignEntryListItem[]; label?: string }) {
  const campaign = useCampaign();
  const navigate = useNavigate();
  return (
    <List aria-label={label}>
      {entries.map((e) => (
        <ListRow
          key={e.id}
          title={e.title}
          badges={<SecretBadge visibility={e.visibility} />}
          subtitle={[campaign.kindLabel(e.kind), e.scope === "world" ? campaign.term("world") : null, e.tags.join(", ") || null].filter(Boolean).join(" · ")}
          value={<span className="muted small">{dateTime(e.updatedAt)}</span>}
          onClick={() => navigate(campaign.entryPath(e.id))}
          ariaLabel={e.title}
        />
      ))}
    </List>
  );
}

export function NewEntry(props: { kinds: CampaignKind[]; preferred?: string; scope?: "campaign" | "world" }) {
  const campaign = useCampaign();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [kindId, setKindId] = useState(props.kinds.find((k) => k.id === props.preferred)?.id ?? props.kinds[0]?.id ?? "");
  const kind = props.kinds.find((k) => k.id === kindId);
  const [scope, setScope] = useState<string>("");
  const action = useAction();
  useDraft(open && title.trim() !== "");
  if (!open)
    return (
      <div className="actions">
        <button type="button" className="button primary" onClick={() => setOpen(true)}>
          Neuer Eintrag
        </button>
      </div>
    );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    let created: string | null = null;
    const ok = await action.run(async () => {
      if (!title.trim()) throw new Error("Der Eintrag braucht einen Titel.");
      const payload: Record<string, unknown> = { kind: kindId, title };
      const wanted = props.scope ?? scope;
      if (wanted) payload.scope = wanted;
      created = (await campaign.apply({ type: "campaign.entry.create", payload })).created;
    });
    if (ok && created) navigate(campaign.entryPath(created));
  };

  return (
    <form className="card stack" onSubmit={(e) => void submit(e)} aria-label="Neuer Eintrag">
      <div className="inline-form two">
        <label className="field">
          <span>Titel</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>Art</span>
          <select value={kindId} onChange={(e) => setKindId(e.target.value)}>
            {props.kinds.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label.de}
              </option>
            ))}
          </select>
        </label>
        {!props.scope && kind && kind.scopes.length > 1 && (
          <label className="field">
            <span>Ablage</span>
            <select value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="">Vorgabe ({kind.scope === "world" ? campaign.term("world") : campaign.term("campaign")})</option>
              <option value="campaign">{campaign.term("campaign")}</option>
              <option value="world">{campaign.term("world")}</option>
            </select>
          </label>
        )}
      </div>
      <p className="muted small">{campaign.mode === "dossier" ? "Im Dossier angelegt ist der Eintrag geheim." : "Der Eintrag ist bekannt."}</p>
      <CampaignError error={action.error} onClose={action.clear} />
      <div className="actions">
        <button type="submit" className="button primary" disabled={action.busy}>
          Anlegen
        </button>
        <button type="button" className="button secondary" onClick={() => setOpen(false)}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

/**
 * Full text search over everything the view sees. The term stays in the page and never goes into the address, where a term
 * searched in the dossier would stay in the history and in the suggestions of the address bar.
 */
export function SearchPage() {
  const campaign = useCampaign();
  const [query, setQuery] = useState("");
  const [input, setInput] = useState("");
  const navigate = useNavigate();
  const hits = useCampaignQuery(
    ["search", query],
    (headers) => api.GET("/api/campaigns/{id}/search", { params: { path: { id: campaign.id }, query: { q: query } }, headers }),
    query.trim().length > 0,
  );
  return (
    <section className="stack">
      <form
        className="row"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(input.trim());
        }}
      >
        <label className="field grow">
          <span className="visually-hidden">Suchbegriff</span>
          <input type="search" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Suchen …" aria-label="Suchbegriff" />
        </label>
        <button type="submit" className="button primary">
          Suchen
        </button>
      </form>
      {hits.isFetching && <p className="muted">Suche …</p>}
      {hits.error && <CampaignError error={hits.error} reload />}
      {hits.data && hits.data.length === 0 && <p className="muted">Nichts gefunden.</p>}
      {hits.data && hits.data.length > 0 && (
        <div className="card">
          <List aria-label="Treffer">
            {hits.data.map((hit, i) => (
              <ListRow
                key={`${hit.entryId}-${hit.sectionId ?? hit.field ?? "head"}-${i}`}
                title={hit.title}
                subtitle={
                  <>
                    <span>{[campaign.kindLabel(hit.kind), hit.heading ?? hit.fieldLabel?.de].filter(Boolean).join(" · ")}</span>
                    <br />
                    <Snippet text={hit.snippet} highlights={hit.highlights} />
                  </>
                }
                onClick={() => navigate(campaign.entryPath(hit.entryId))}
                ariaLabel={hit.title}
              />
            ))}
          </List>
        </div>
      )}
    </section>
  );
}

function Snippet({ text, highlights }: { text: string; highlights: { start: number; length: number }[] }) {
  const parts: (string | { mark: string })[] = [];
  let at = 0;
  for (const h of [...highlights].sort((a, b) => a.start - b.start)) {
    if (h.start < at) continue;
    parts.push(text.slice(at, h.start), { mark: text.slice(h.start, h.start + h.length) });
    at = h.start + h.length;
  }
  parts.push(text.slice(at));
  return (
    <span className="snippet">
      {parts.map((p, i) => (typeof p === "string" ? p : <mark key={i}>{p.mark}</mark>))}
    </span>
  );
}

/** The entries of the world, facts first; they are shared by the campaigns of the same system. */
export function WorldPage() {
  const campaign = useCampaign();
  const entries = useEntries({ scope: "world" });
  const facts = (entries.data ?? []).filter((e) => e.kind === "fact");
  const others = (entries.data ?? []).filter((e) => e.kind !== "fact");
  const worldKinds = campaign.head.kinds.filter((k) => k.creatable && k.scopes.includes("world"));
  return (
    <section className="stack">
      <h2>
        {campaign.term("world")}: {campaign.head.world?.name ?? "–"}
      </h2>
      <p className="muted small">Was hier steht, gilt für alle Kampagnen dieser Welt.</p>
      {campaign.canWrite && <NewEntry kinds={worldKinds} preferred="fact" scope="world" />}
      {entries.isLoading && <p className="muted">Lade …</p>}
      {entries.error && <CampaignError error={entries.error} reload />}
      <div className="card">
        <h3>{campaign.kindLabel("fact")}</h3>
        {facts.length === 0 ? <p className="muted small">Noch keine.</p> : <EntryList entries={facts} label={campaign.kindLabel("fact")} />}
      </div>
      {others.length > 0 && (
        <div className="card">
          <h3>Weitere Einträge</h3>
          <EntryList entries={others} label="Weitere Einträge der Welt" />
        </div>
      )}
    </section>
  );
}
